import { Controller } from "@hotwired/stimulus"

// Light, dark, or follow the system. The choice lives in localStorage under
// the same key the legacy PWA used (winote_theme) so nobody's preference is
// lost at cutover. "system" is the default and is stored as no key at all.
// layouts/_head.html.erb applies the stored choice inline before first paint;
// this controller keeps it current afterwards.
const STORAGE_KEY = "winote_theme"
const THEME_COLORS = { light: "#F5F8F0", dark: "#15101C" }
const THEME_EVENT = "winote:theme-changed"

export default class extends Controller {
  connect() {
    this.media = matchMedia("(prefers-color-scheme: dark)")
    this.onSystemChange = () => { if (this.preference() === "system") this.apply() }
    this.media.addEventListener("change", this.onSystemChange)
    // This controller sits on <html>, which Turbo keeps across visits, so
    // re-apply on each one: the page arrived at may lock the theme, or not.
    this.onVisit = () => this.apply()
    document.addEventListener("turbo:load", this.onVisit)
    this.apply()
  }

  disconnect() {
    this.media.removeEventListener("change", this.onSystemChange)
    document.removeEventListener("turbo:load", this.onVisit)
  }

  light() { this.set("light") }
  dark() { this.set("dark") }
  system() { this.set("system") }

  set(preference) {
    // Keep the current tab interactive even when a privacy mode or a storage
    // policy rejects reads/writes. A successful write remains authoritative;
    // this fallback exists only for the lifetime of this controller instance.
    this.fallbackPreference = preference
    try {
      if (preference === "system") localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, preference)
    } catch (e) { /* private mode: best effort */ }
    this.apply()
  }

  preference() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored === "light" || stored === "dark") return stored
      return this.fallbackPreference || "system"
    } catch (e) { return this.fallbackPreference || "system" }
  }

  apply() {
    const preference = this.preference()
    // A layout that only comes in one theme locks it with a meta tag (the
    // marketing site is light-only). The stored preference is left alone, so
    // the apps still honour it.
    const lock = document.querySelector('meta[name="winote-theme-lock"]')?.content
    const resolved = lock || (preference === "system" ? (this.media.matches ? "dark" : "light") : preference)
    document.documentElement.setAttribute("data-bs-theme", resolved)
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLORS[resolved])
    // The profile's System / Light / Dark picker: mark the stored choice.
    document.querySelectorAll("[data-theme-option]").forEach((option) => {
      const checked = option.dataset.themeOption === preference
      if (option.getAttribute("role") === "radio") option.setAttribute("aria-checked", String(checked))
      else option.setAttribute("aria-pressed", String(checked))
      option.classList.toggle("active", checked)
    })
    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { theme: resolved } }))
  }
}
